import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { Switch } from "@components/ui/selection/Switch";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import DropDown from "@components/ui/dropdown/DropDown";
import OrdreStatusDropdown from "@components/ui/dropdown/OrdreStatusDropdown";
import AssignDropdown from "@components/ui/dropdown/AssignDropdown";
import InlineTagEditor from "@components/ui/tags/InlineTagEditor";
import DatePicker from "@components/ui/selection/DatePicker";
import DateTimePicker from "@components/ui/selection/DateTimePicker";
import CheckBox from "@components/ui/selection/CheckBox";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { useState, useRef, useEffect, useMemo } from "react";
import Avvvatars from "avvvatars-react";
import ChatPanel from "@components/chat/ChatPanel";
import { ordresApi } from "@services/api/ordres";
import { appointmentsApi, type ReceptionConflictResult, type ReceptionConflictEvent } from "@services/api/appointments";
import { DocumentPreviewModal } from "@components/ui/DocumentPreviewModal";
import { dossiersApi } from "@services/api/dossiers";
import { useOrdreStore } from "@store/ordreStore";
import { usePlanningStore } from "@store/planningStore";
import { navigateTo } from "context/navigation.service";
import { useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import VehicleImage from "@components/ui/VehicleImage";
import VehicleEditModal from "@components/modals/VehicleEditModal";
import ContactFormModal from "@components/modals/ContactFormModal";
import type {
  Ordre,
  OrdreStatus,
  OrdreIntervention,
  OrdreDocument,
} from "types/ordre";
import type { DossierType } from "types/dossier";
import type { Contact } from "types/contact";
import type { Dossier } from "types/dossier";
import type { ClientVehicle } from "types/client_vehicle";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import { atelierApi } from "@services/api/atelier";
import { recallCampaignsApi } from "@services/api/recallCampaigns";
import RestitutionChangeConfirmModal from "@components/modals/RestitutionChangeConfirmModal";
import SecondaryOrdreModal from "@components/modals/SecondaryOrdreModal";
import OrdreActionsMenu from "@components/ui/OrdreActionsMenu";
import { FactureUploadModal, RentalVehicleModal, TrackingPreviewModal } from "@components/modals/OrdreActionModals";
import PaymentRequestModal from "@components/modals/PaymentRequestModal";
import EstimateExtractionModal from "@components/modals/EstimateExtractionModal";
import ServiceBoxTransferModal from "@components/modals/ServiceBoxTransferModal";
import ContratLocationModal from "@components/modals/ContratLocationModal";
import DevisReminderModal from "@components/modals/DevisReminderModal";
import { paymentsApi } from "@services/api/payments";
import { vehiclesApi } from "@services/api/vehicles";
import { estimateExtractionApi } from "@services/api/estimateExtraction";
import { serviceboxProxy, type ForfaitChoice } from "@services/serviceboxProxy";
import { ORDRE_FLAG_CONFIG } from "types/ordre";
import type { OrdreFlag } from "types/ordre";
import type { Payment } from "types/payment";
import type { Vehicle } from "types/garage/vehicules";
import type { EstimateExtraction } from "types/estimateExtraction";
import ForceConfirmModal from "@components/modals/ForceConfirmModal";
import { bookingsApi, type SlotValidationResult } from "@services/api/bookings";
import { isTimeOffGrid } from "@utils/timeGrid";

/** Minimum reception→restitution window (one 15-min slot) to forbid a zero-duration window. */
const MIN_WINDOW_SECONDS = 15 * 60;

/** Convert epoch seconds to local { date: "YYYY-MM-DD", time: "HH:MM" }. */
function secondsToDateTime(seconds: number): { date: string; time: string } {
  const d = new Date(seconds * 1000);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return { date, time };
}

// ─── Constants ─────────────────────────────────────────────

const TABS = [
  { value: "ordre", label: "Ordre" },
  { value: "interventions", label: "Interventions" },
  { value: "contacts", label: "Contacts" },
  { value: "vehicules", label: "Véhicules" },
  { value: "documents", label: "Documents" },
  { value: "communications", label: "Chat" },
];

const STATUS_OPTIONS: { value: OrdreStatus; label: string }[] = [
  { value: "a_preparer", label: "À préparer" },
  { value: "devis_initial", label: "Devis initial" },
  { value: "commande", label: "Commandé" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réceptionné" },
  { value: "en_cours", label: "En cours" },
  { value: "devis_supp", label: "Devis supp." },
  { value: "accord", label: "Accord" },
  { value: "acompte", label: "Acompte" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "solde", label: "Solde" },
  { value: "solde_paye", label: "Solde payé" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
];


function formatHour(h: number): string {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h${mins.toString().padStart(2, "0")}`;
}

function formatTimestamp(ts?: { seconds: number }): string {
  if (!ts) return "—";
  return new Date(ts.seconds * 1000).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ─── Sub-modal for adding items ────────────────────────────

function AddSubModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <BaseModal
      title={title}
      startIcon={<Icon1 name="add" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">{children}</div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label="Ajouter" onClick={onClose} />
      </div>
    </BaseModal>
  );
}

// ─── Tab content components ────────────────────────────────

function OrdreTab({
  form,
  update,
  assignedWorkers,
  linkedVehicle,
  dossierType,
  onDossierTypeChange,
  recalls,
  receptionConflict,
  onSwitchReception,
  receptionDisabledSlots,
  restitutionDisabledSlots,
}: {
  form: Ordre;
  update: <K extends keyof Ordre>(key: K, value: Ordre[K]) => void;
  assignedWorkers: { name: string; specialty: string }[];
  linkedVehicle?: ClientVehicle | null;
  dossierType?: DossierType;
  onDossierTypeChange?: (type: DossierType | undefined) => void;
  recalls: { code: string; libelle: string; status: string }[];
  receptionConflict?: ReceptionConflictResult | null;
  onSwitchReception?: (ev: ReceptionConflictEvent, recId: string) => void;
  receptionDisabledSlots?: string[];
  restitutionDisabledSlots?: string[];
}) {
  // Auto-fill modèle from linked vehicle when vehicle changes
  const prevVehicleIdRef = useRef(linkedVehicle?.id);
  useEffect(() => {
    if (linkedVehicle && linkedVehicle.id !== prevVehicleIdRef.current) {
      const vehicleModele = [linkedVehicle.marque, linkedVehicle.modele].filter(Boolean).join(" ");
      if (vehicleModele) {
        update("modele", vehicleModele);
      }
    }
    prevVehicleIdRef.current = linkedVehicle?.id;
  }, [linkedVehicle?.id]);

  // Auto-set restitution when réception date (day) changes.
  // If only time changed (same day): only update restitution if reception time
  // goes past the current restitution time on that same day.
  const prevReceptionRef = useRef(form.reception_date?.seconds);
  useEffect(() => {
    const receptionSeconds = form.reception_date?.seconds;
    const prevSeconds = prevReceptionRef.current;
    if (
      receptionSeconds &&
      receptionSeconds !== prevSeconds
    ) {
      const receptionDate = new Date(receptionSeconds * 1000);
      const prevDate = prevSeconds ? new Date(prevSeconds * 1000) : null;
      const sameDayAsBefore =
        prevDate &&
        receptionDate.getFullYear() === prevDate.getFullYear() &&
        receptionDate.getMonth() === prevDate.getMonth() &&
        receptionDate.getDate() === prevDate.getDate();

      if (sameDayAsBefore) {
        // Only time changed — adjust restitution only if reception goes past it
        // and restitution is on the same day
        const restitutionSeconds = form.restitution?.seconds;
        if (restitutionSeconds) {
          const restitutionDate = new Date(restitutionSeconds * 1000);
          const sameDay =
            receptionDate.getFullYear() === restitutionDate.getFullYear() &&
            receptionDate.getMonth() === restitutionDate.getMonth() &&
            receptionDate.getDate() === restitutionDate.getDate();
          if (sameDay && receptionSeconds >= restitutionSeconds) {
            const twoHoursInSeconds = 2 * 60 * 60;
            update("restitution", { seconds: receptionSeconds + twoHoursInSeconds });
          }
        }
      } else {
        // Day changed — reset restitution to reception + 2h
        const twoHoursInSeconds = 2 * 60 * 60;
        update("restitution", { seconds: receptionSeconds + twoHoursInSeconds });
      }
    }
    prevReceptionRef.current = receptionSeconds;
  }, [form.reception_date?.seconds]);


  return (
    <div className="flex flex-col gap-3 p-4">
      {/* Status + Assign + Tags — compact row like OrdreDetailScreen */}
      <div className="flex flex-row items-center gap-2 flex-wrap">
        <OrdreStatusDropdown
          currentStatus={form.status}
          onStatusChange={(v) => update("status", v)}
        />
        <AssignDropdown
          assignedTo={form.assigned_to}
          onAssign={(userId) => update("assigned_to", userId || "")}
        />
        {assignedWorkers.map((w) => (
          <span
            key={w.name}
            className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full px-3 py-1"
            style={{
              background: w.specialty === "carrosserie" ? "#FEF3C7" : w.specialty === "mecanique" ? "#DBEAFE" : "#F3F4F6",
              color: w.specialty === "carrosserie" ? "#92400E" : w.specialty === "mecanique" ? "#1E40AF" : "#374151",
            }}
          >
            <Icon1 name="side-ordres" size={12} color={w.specialty === "carrosserie" ? "#92400E" : w.specialty === "mecanique" ? "#1E40AF" : "#374151"} />
            {w.name}
          </span>
        ))}
        {form.amount != null && (
          <span className="text-h3 font-medium rounded-full px-3 py-1" style={{ background: "#EDE9FE", color: "#5B21B6" }}>
            {form.amount.toFixed(2)} € TTC
          </span>
        )}
      </div>
      <InlineTagEditor
        scope="ordre"
        tagIds={form.tag_ids || []}
        onSave={(ids) => update("tag_ids", ids)}
      />

      {recalls.length > 0 && (
        <div className="flex flex-col gap-1 px-3 py-2 rounded-lg" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
          <div className="flex flex-row items-center gap-2">
            <Icon1 name="warning" size={14} color="#DC2626" />
            <span className="text-h3 font-semibold" style={{ color: "#DC2626" }}>
              Campagne de rappel ({recalls.length})
            </span>
          </div>
          {recalls.map((c) => (
            <div key={c.code || c.libelle} className="flex flex-row items-center gap-2">
              <span className="text-h4" style={{ color: "#991B1B" }}>{c.code} — {c.libelle}</span>
              <span
                className="text-h5 font-medium rounded-full px-2 py-0.5"
                style={{
                  background: c.status === "treated" ? "#D1FAE5" : c.status === "acknowledged" ? "#FEF3C7" : "#FEE2E2",
                  color: c.status === "treated" ? "#065F46" : c.status === "acknowledged" ? "#92400E" : "#991B1B",
                }}
              >
                {c.status === "treated" ? "Traité" : c.status === "acknowledged" ? "Pris en compte" : "Détecté"}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="N° Ordre" value={form.num} onChange={(v) => update("num", v)} />
        </div>
        <div className="flex-1">
          <TextField label="Nom" value={form.name} onChange={(v) => update("name", v)} />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <DropDown
            label="Type de dossier"
            value={dossierType || ""}
            options={[
              { value: "", label: "Non défini" },
              { value: "particulier", label: "Particulier" },
              { value: "professionnel", label: "Professionnel" },
              { value: "loueur", label: "Loueur" },
              { value: "garantie", label: "Garantie" },
              { value: "occasion", label: "Véhicule occasion" },
              { value: "interne", label: "Véhicule interne" },
            ]}
            onSelect={(v) => onDossierTypeChange?.((v || undefined) as DossierType | undefined)}
          />
        </div>
        <div className="flex-1">
          <TextField
            label="Montant TTC (€)"
            value={form.amount != null ? `${form.amount.toFixed(2)} €` : ""}
            placeholder="Pas encore de devis récupéré de ServiceBox"
            onChange={() => {}}
            disabled
          />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Immatriculation" value={form.immatriculation || ""} onChange={(v) => update("immatriculation", v)} />
        </div>
        <div className="flex-1">
          <TextField label="Modèle" value={form.modele || ""} onChange={(v) => update("modele", v)} />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <DateTimePicker
            label="Date réception"
            seconds={form.reception_date?.seconds || null}
            disabledSlots={receptionDisabledSlots}
            onChange={(s) => {
              update("reception_date", { seconds: s });
              // Restitution must stay strictly after reception (min one slot)
              if (form.restitution?.seconds && form.restitution.seconds < s + MIN_WINDOW_SECONDS) {
                update("restitution", { seconds: s + MIN_WINDOW_SECONDS });
              }
            }}
          />
        </div>
        <div className="flex-1">
          <DateTimePicker
            label="Date restitution"
            seconds={form.restitution?.seconds || null}
            disabledSlots={restitutionDisabledSlots}
            onChange={(s) => {
              const rec = form.reception_date?.seconds || 0;
              // Restitution can't be at or before reception — enforce a minimum window
              const minSeconds = rec ? rec + MIN_WINDOW_SECONDS : 0;
              update("restitution", { seconds: Math.max(s, minSeconds) });
            }}
          />
        </div>
      </div>
      {!form.restitution?.seconds && (
        <span className="text-h4 text-red-500">La date de restitution est obligatoire</span>
      )}
      {(isTimeOffGrid(form.reception_date?.seconds) || isTimeOffGrid(form.restitution?.seconds)) && (
        <span className="text-h4 text-orange-600">
          Horaire hors créneau de 7,5 min
          {isTimeOffGrid(form.reception_date?.seconds) ? ` (réception ${new Date((form.reception_date!.seconds) * 1000).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })})` : ""}
          {isTimeOffGrid(form.restitution?.seconds) ? ` (restitution ${new Date((form.restitution!.seconds) * 1000).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })})` : ""}
          {" "}— peut être refusé par ServiceBox.
        </span>
      )}
      {/* ServiceBox slot-collision warning (reception/restitution on a taken column) */}
      {[receptionConflict?.reception, receptionConflict?.restitution]
        .filter((e): e is ReceptionConflictEvent => !!e && e.conflict)
        .map((ev) => (
          <div
            key={ev.type}
            className="flex flex-col gap-2 p-3 rounded-lg"
            style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}
          >
            <span className="text-h4 font-medium" style={{ color: "#92400E" }}>
              ⚠ Le créneau de {ev.type === "reception" ? "réception" : "restitution"} ({ev.conflict_with?.time}) est déjà
              occupé en {ev.category_label}
              {ev.conflict_with?.client_name ? ` par ${ev.conflict_with.client_name}` : ""}
              {ev.conflict_with?.plate ? ` (${ev.conflict_with.plate})` : ""}. Le transfert ServiceBox sera refusé.
            </span>
            {ev.free_receptions.length > 0 ? (
              <div className="flex flex-row flex-wrap gap-2">
                {ev.free_receptions.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => onSwitchReception?.(ev, r.id)}
                    className="px-3 py-1.5 rounded-lg text-h4 font-medium transition-opacity hover:opacity-80"
                    style={{ background: "#FFFFFF", border: "1px solid #FCD34D", color: "#92400E" }}
                  >
                    Basculer sur {r.label}
                  </button>
                ))}
              </div>
            ) : (
              <span className="text-h5" style={{ color: "#92400E", opacity: 0.85 }}>
                Aucune autre réception libre sur ce créneau.
              </span>
            )}
          </div>
        ))}
      <AreaTextField
        label="Notes"
        value={form.notes || ""}
        onChange={(v) => update("notes", v)}
        rows={3}
      />
    </div>
  );
}

// ─── Intervention Picker Modal ─────────────────────────────

const INTERVENTION_TYPE_LABELS: Record<string, string> = {
  entretien: "Entretien / Révision", reparation: "Réparation / Panne", diagnostic: "Diagnostic",
  controle_technique: "Contrôle technique", pneumatique: "Pneus", carrosserie: "Carrosserie / Vitrage",
};
const INTERVENTION_TYPE_OPTIONS = Object.entries(INTERVENTION_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

function InterventionPickerModal({ onClose, onAdd, configuredInterventions, onCatalogUpdated, group = "initial" }: {
  onClose: () => void;
  onAdd: (inters: OrdreIntervention[]) => void;
  configuredInterventions: Intervention[];
  onCatalogUpdated: (items: Intervention[]) => void;
  group?: string;
}) {
  const workers = useAtelierStore((s) => s.workers);
  const workerOptions = [
    { value: "", label: "Non assigné" },
    ...workers.map((w) => ({ value: w.id, label: w.name })),
  ];

  const [mode, setMode] = useState<"catalog" | "new">("catalog");
  const [selectedItems, setSelectedItems] = useState<Intervention[]>([]);
  const [search, setSearch] = useState("");
  const [label, setLabel] = useState("");
  const [estimatedH, setEstimatedH] = useState("");
  const [assignedTo, setAssignedTo] = useState("");

  const resetForm = () => {
    setSelectedItems([]); setLabel(""); setEstimatedH(""); setAssignedTo(""); setSearch("");
  };

  // Multi-select: toggle an item. When exactly one is selected, expose its
  // editable detail (libellé / heures / assigné). With several selected, each is
  // added with its catalogue defaults.
  const toggleCatalog = (item: Intervention) => {
    setSelectedItems((prev) => {
      const exists = prev.some((p) => p.id === item.id);
      const next = exists ? prev.filter((p) => p.id !== item.id) : [...prev, item];
      if (next.length === 1) {
        const only = next[0];
        const h = +(only.estimated_minutes / 60).toFixed(2);
        setLabel(only.name); setEstimatedH(String(h)); setAssignedTo("");
      } else {
        setLabel(""); setEstimatedH(""); setAssignedTo("");
      }
      return next;
    });
  };

  const canAdd = mode === "catalog" ? selectedItems.length > 0 : label.trim() !== "";

  const handleAdd = () => {
    if (!canAdd) return;
    if (mode === "new") {
      // Intervention specific to THIS ordre only — not saved to the catalog.
      onAdd([{
        id: `i-${Date.now()}`,
        label,
        estimated_hours: parseFloat(estimatedH) || 0,
        assigned_to: assignedTo,
        group,
      }]);
    } else {
      const single = selectedItems.length === 1;
      const inters: OrdreIntervention[] = selectedItems.map((item, idx) => {
        const h = +(item.estimated_minutes / 60).toFixed(2);
        return {
          id: `i-${Date.now()}-${idx}`,
          label: single ? label : item.name,
          estimated_hours: single ? (parseFloat(estimatedH) || h) : h,
          assigned_to: single ? assignedTo : "",
          group,
          forfait_code: item.forfait_code || undefined,
        };
      });
      onAdd(inters);
    }
    onClose();
  };

  return (
    <BaseModal title="Ajouter une intervention" startIcon={<Icon1 name="plus" size={20} />} onClose={onClose} width="max-w-lg">
      <div className="p-4 flex flex-col gap-4">
        <ChunkyTabs
          activeTab={mode}
          tabs={[{ value: "catalog", label: "Depuis le catalogue" }, { value: "new", label: "Nouvelle" }]}
          setActiveTab={(t) => { setMode(t as "catalog" | "new"); resetForm(); }}
        />

        {mode === "catalog" && (
          <div className="flex flex-col gap-3">
            <TextField placeholder="Rechercher une intervention…" value={search} onChange={setSearch} />
            <div className="overflow-y-auto rounded-lg border border-neutral-200" style={{ maxHeight: 220 }}>
              {Object.entries(INTERVENTION_TYPE_LABELS).map(([cat, catLabel]) => {
                const q = search.trim().toLowerCase();
                const items = configuredInterventions
                  .filter((i) => i.type === cat && i.active && (!q || i.name.toLowerCase().includes(q)))
                  .sort((a, b) => a.name.localeCompare(b.name, "fr"));
                if (!items.length) return null;
                return (
                  <div key={cat}>
                    <div className="px-3 py-1 text-h5 font-semibold text-neutral-400 bg-neutral-50 sticky top-0">{catLabel}</div>
                    {items.map((item) => {
                      const h = +(item.estimated_minutes / 60).toFixed(2);
                      const isSelected = selectedItems.some((s) => s.id === item.id);
                      return (
                        <button
                          key={item.id}
                          onClick={() => toggleCatalog(item)}
                          className={`flex flex-row items-center gap-2 w-full px-3 py-2 text-left transition-colors ${isSelected ? "bg-indigo-50" : "hover:bg-neutral-50"}`}
                        >
                          <span
                            className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0"
                            style={{ borderColor: isSelected ? "#6366f1" : "#D4D4D4", background: isSelected ? "#6366f1" : "transparent" }}
                          >
                            {isSelected && (
                              <svg width={9} height={7} viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" /></svg>
                            )}
                          </span>
                          <span className={`text-h3 flex-1 ${isSelected ? "text-indigo-700 font-medium" : "text-neutral-600"}`}>{item.name}</span>
                          <span className="text-h4 text-neutral-400 ml-2 flex-shrink-0">{h}h</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              {(() => {
                const q = search.trim().toLowerCase();
                const matches = configuredInterventions.filter((i) => i.active && (!q || i.name.toLowerCase().includes(q)));
                if (matches.length > 0) return null;
                return (
                  <div className="px-3 py-6 text-h3 text-neutral-400 text-center">
                    {q ? "Aucune intervention trouvée" : "Aucune intervention dans le catalogue"}
                  </div>
                );
              })()}
            </div>
            {selectedItems.length === 1 && (
              <div className="flex flex-col gap-3 pt-2 border-t border-neutral-100">
                <TextField label="Libellé" value={label} onChange={setLabel} />
                <div className="flex flex-row gap-2">
                  <div className="flex-1">
                    <TextField label="Heures estimées" type="number" value={estimatedH} onChange={setEstimatedH} />
                  </div>
                  <div className="flex-1">
                    <DropDown label="Assigné à" options={workerOptions} value={assignedTo} onSelect={(v) => setAssignedTo(String(v))} />
                  </div>
                </div>
              </div>
            )}
            {selectedItems.length > 1 && (
              <div className="flex flex-col gap-1 pt-2 border-t border-neutral-100">
                <span className="text-h3 font-medium text-indigo-700">{selectedItems.length} interventions sélectionnées</span>
                <span className="text-h4 text-neutral-400">{selectedItems.map((s) => s.name).join(", ")}</span>
                <span className="text-h5 text-neutral-400">Chacune sera ajoutée avec sa durée estimée. Vous pourrez assigner les mécaniciens depuis l'atelier.</span>
              </div>
            )}
          </div>
        )}

        {mode === "new" && (
          <div className="flex flex-col gap-3">
            <TextField label="Libellé" value={label} onChange={setLabel} />
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <TextField label="Durée estimée (h)" type="number" value={estimatedH} onChange={setEstimatedH} />
              </div>
              <div className="flex-1">
                <DropDown label="Assigné à" options={workerOptions} value={assignedTo} onSelect={(v) => setAssignedTo(String(v))} />
              </div>
            </div>
            <span className="text-h4 text-neutral-400">Intervention spécifique à cet ordre (non ajoutée au catalogue).</span>
          </div>
        )}
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label={mode === "catalog" && selectedItems.length > 1 ? `Ajouter (${selectedItems.length})` : "Ajouter"} onClick={handleAdd} disabled={!canAdd} />
      </div>
    </BaseModal>
  );
}

// ContactFormModal is now imported from @components/modals/ContactFormModal

function AddVehicleModal({ onClose }: { onClose: () => void }) {
  const [plate, setPlate] = useState("");
  const [marque, setMarque] = useState("");
  const [modele, setModele] = useState("");
  const [vin, setVin] = useState("");
  return (
    <AddSubModal title="Ajouter un véhicule" onClose={onClose}>
      <TextField label="Immatriculation" value={plate} onChange={setPlate} />
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Marque" value={marque} onChange={setMarque} />
        </div>
        <div className="flex-1">
          <TextField label="Modèle" value={modele} onChange={setModele} />
        </div>
      </div>
      <TextField label="VIN" value={vin} onChange={setVin} />
    </AddSubModal>
  );
}

// ─── Tab content components (Interventions, Atelier, Contacts, Vehicules, Documents) ──

const GROUP_STATUS_LABELS: Record<string, string> = {
  draft: "Brouillon",
  devis_sent: "Devis envoye",
  devis_signed: "Devis signe",
  ready: "Pret",
};

const GROUP_STATUS_COLORS: Record<string, string> = {
  draft: "bg-neutral-100 text-neutral-500",
  devis_sent: "bg-blue-100 text-blue-700",
  devis_signed: "bg-amber-100 text-amber-700",
  ready: "bg-green-100 text-green-700",
};

interface AtelierChargeDisplay {
  id: string;
  worker_name: string;
  specialty: string;
  date: string;
  start_hour: number;
  duration_hours: number;
  is_placed: boolean;
}

function ModalGroupSection({
  title,
  group,
  interventions,
  groupStatus,
  onAdd,
  onRemove,
}: {
  title: string;
  group: string;
  interventions: OrdreIntervention[];
  groupStatus?: { status: string };
  onAdd: (group: string) => void;
  onRemove: (idx: number) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const status = groupStatus?.status || "draft";
  const workers = useAtelierStore((s) => s.workers);
  const getWorkerName = (id?: string) => {
    if (!id) return "Non assigné";
    const w = workers.find((w) => w.id === id);
    return w ? w.name : id;
  };

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div
        className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex flex-row items-center gap-2">
          <Icon1 name={collapsed ? "right" : "down"} size={12} color="#525252" />
          <span className="text-h3 font-medium">{title}</span>
          <span className="text-h4 text-neutral-400">({interventions.length})</span>
        </div>
        <span className={`text-h5 font-medium px-2 py-0.5 rounded-full ${GROUP_STATUS_COLORS[status] || ""}`}>
          {GROUP_STATUS_LABELS[status] || status}
        </span>
      </div>
      {!collapsed && (
        <div className="p-3 flex flex-col gap-2">
          {interventions.map((inter, i) => (
            <div key={inter.id || i} className="flex flex-row items-center justify-between bg-white rounded-lg border border-neutral-200 px-3 py-2">
              <div className="flex flex-col">
                <span className="text-h3 font-medium">{inter.label || "Nouvelle intervention"}</span>
                <span className="text-h4 text-neutral-400">
                  {inter.estimated_hours}h — {getWorkerName(inter.assigned_to)}
                </span>
              </div>
              <button onClick={() => onRemove(i)} className="p-1 rounded hover:bg-neutral-100 transition-colors flex-shrink-0">
                <Icon1 name="close" size={14} color="#A1A1A1" />
              </button>
            </div>
          ))}
          {interventions.length === 0 && (
            <span className="text-h3 text-neutral-400 py-3 text-center">Aucune intervention</span>
          )}
          <div className="mt-1">
            <SecondaryButton variant="small" startIcon={<Icon1 name="plus" size={14} />} label="Ajouter" onClick={() => onAdd(group)} />
          </div>
        </div>
      )}
    </div>
  );
}

const SPECIALTY_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  mecanique:     { bg: "#DBEAFE", color: "#1E40AF", label: "Méca" },
  carrosserie:   { bg: "#FEF3C7", color: "#92400E", label: "Carross." },
  peinture:      { bg: "#FEE2E2", color: "#DC2626", label: "Peinture" },
  diagnostique:  { bg: "#D1FAE5", color: "#059669", label: "Diag." },
  service_rapide:{ bg: "#EDE9FE", color: "#6F61FF", label: "Serv. rapide" },
  campagnes:     { bg: "#FFEDD5", color: "#EA580C", label: "Campagnes" },
  autre:         { bg: "#F3F4F6", color: "#374151", label: "Autre" },
};

function AtelierPlanningSection({ charges }: { charges: AtelierChargeDisplay[] }) {
  const [collapsed, setCollapsed] = useState(false);
  const placed   = charges.filter((c) => c.is_placed);
  const unplaced = charges.filter((c) => !c.is_placed);

  const statusBadge = charges.length === 0 ? null : unplaced.length > 0
    ? <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{unplaced.length} a planifier</span>
    : <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Planifie</span>;

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div
        className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex flex-row items-center gap-2">
          <Icon1 name={collapsed ? "right" : "down"} size={12} color="#525252" />
          <span className="text-h3 font-medium">Planification atelier</span>
          <span className="text-h4 text-neutral-400">({charges.length})</span>
          {statusBadge}
        </div>
      </div>

      {!collapsed && (
        <div className="divide-y divide-neutral-100">
          {charges.length === 0 && (
            <div className="px-3 py-4 text-center text-h3 text-neutral-400">Aucune charge atelier</div>
          )}

          {placed.map((c) => {
            const sp = SPECIALTY_STYLE[c.specialty] || SPECIALTY_STYLE.autre;
            return (
              <div key={c.id} className="flex flex-row items-center gap-3 px-3 py-2">
                <span className="text-h4 font-medium px-2.5 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {c.worker_name}
                </span>
                <span className="text-h5 font-medium px-2 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {sp.label}
                </span>
                <span className="text-h3 text-neutral-600">{c.date.split("-").reverse().join("/")}</span>
                <span className="text-h3 text-neutral-500">{formatHour(c.start_hour)} — {formatHour(c.start_hour + c.duration_hours)}</span>
                <span className="text-h4 text-neutral-400 ml-auto">{c.duration_hours}h</span>
              </div>
            );
          })}

          {unplaced.map((c) => {
            const sp = SPECIALTY_STYLE[c.specialty] || SPECIALTY_STYLE.autre;
            return (
              <div key={c.id} className="flex flex-row items-center gap-3 px-3 py-2">
                <span className="text-h4 font-medium px-2.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700">
                  A planifier
                </span>
                <span className="text-h5 font-medium px-2 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {sp.label}
                </span>
                <span className="text-h4 text-neutral-400 ml-auto">{c.duration_hours}h</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InterventionsTab({
  interventions,
  interventionGroups,
  atelierCharges,
  onAdd,
  onRemove,
}: {
  interventions: Ordre["interventions"];
  interventionGroups?: Ordre["intervention_groups"];
  atelierCharges: AtelierChargeDisplay[];
  onAdd: (group: string) => void;
  onRemove: (idx: number) => void;
}) {
  // Compute additional group keys
  const additionalKeys = useMemo(() => {
    const keys = new Set<string>();
    if (interventionGroups) {
      Object.keys(interventionGroups).filter((k) => k.startsWith("additional_")).forEach((k) => keys.add(k));
    }
    interventions.forEach((i) => { if (i.group?.startsWith("additional_")) keys.add(i.group); });
    // Legacy compat
    if (interventionGroups?.additional || interventions.some((i) => i.group === "additional")) {
      keys.add("additional_0");
    }
    return Array.from(keys).sort();
  }, [interventionGroups, interventions]);

  const initialInterventions = interventions.filter((i) => (i.group || "initial") === "initial");

  const getAdditionalInterventions = (key: string) =>
    interventions.filter((i) =>
      i.group === key || (key === "additional_0" && i.group === "additional"),
    );

  const getGroupStatus = (key: string) => {
    if (!interventionGroups) return undefined;
    return interventionGroups[key] || (key === "additional_0" ? interventionGroups["additional"] : undefined);
  };

  return (
    <div className="flex flex-col p-4 gap-3">
      <ModalGroupSection
        title="Interventions initiales"
        group="initial"
        interventions={initialInterventions}
        groupStatus={interventionGroups?.initial}
        onAdd={onAdd}
        onRemove={(i) => {
          const inter = initialInterventions[i];
          const globalIdx = interventions.findIndex((x) => x.id === inter.id);
          if (globalIdx >= 0) onRemove(globalIdx);
        }}
      />

      {additionalKeys.map((key, idx) => {
        const groupInterventions = getAdditionalInterventions(key);
        return (
          <ModalGroupSection
            key={key}
            title={`Interventions supplementaires${additionalKeys.length > 1 ? ` #${idx + 1}` : ""}`}
            group={key}
            interventions={groupInterventions}
            groupStatus={getGroupStatus(key)}
            onAdd={onAdd}
            onRemove={(i) => {
              const inter = groupInterventions[i];
              const globalIdx = interventions.findIndex((x) => x.id === inter.id);
              if (globalIdx >= 0) onRemove(globalIdx);
            }}
          />
        );
      })}

      <AtelierPlanningSection charges={atelierCharges} />
    </div>
  );
}

function ContactsTab({ contacts, onAdd, onEdit }: { contacts: Ordre["contacts"]; onAdd: () => void; onEdit: (contact: Contact) => void }) {
  return (
    <div className="flex flex-col p-4 gap-2">
      <div className="flex flex-row items-center justify-between">
        <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Contacts</span>
        <SecondaryButton variant="small" onClick={onAdd}>
          <span className="flex flex-row items-center gap-1">
            <Icon1 name="plus" size={12} />
            Ajouter
          </span>
        </SecondaryButton>
      </div>
      {(contacts || []).map((c, i) => {
        const displayName = [c.prenom, c.nom].filter(Boolean).join(" ") || "—";
        return (
          <div key={i} className="flex flex-row items-center gap-3 bg-white rounded-lg border border-neutral-200 px-3 py-2">
            <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
              <Avvvatars value={displayName} style="shape" size={32} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <span className="text-h3 font-medium" style={{ fontFamily: "Poppins" }}>{displayName}</span>
              <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>{c.role || "—"} · {c.phone || c.email || "—"}</span>
            </div>
            <button
              onClick={() => onEdit(c)}
              className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
              style={{ color: "#6366f1" }}
            >
              Modifier
            </button>
          </div>
        );
      })}
      {(!contacts || contacts.length === 0) && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun contact</span>
      )}
    </div>
  );
}

const COLORIS_OPTIONS = [
  { value: "noir", label: "Noir" }, { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" }, { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" }, { value: "vert", label: "Vert" },
  { value: "autre", label: "Autre" },
];

// VehicleEditModal is now imported from @components/modals/VehicleEditModal

function VehiculesTab({
  dossierVehicles,
  selectedVehicleId,
  onSelect,
  onEdit,
}: {
  dossierVehicles: Array<{ id: string; plate?: string; marque?: string; modele?: string; coloris?: string; vin?: string; km?: number; mec?: number }>;
  selectedVehicleId?: string;
  onSelect: (vehicleId: string) => void;
  onEdit: (vehicle: any) => void;
}) {

  return (
    <div className="flex flex-col p-4 gap-2">
      <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Véhicules du dossier</span>
      {dossierVehicles.map((v) => {
        const isSelected = v.id === selectedVehicleId;
        return (
          <div
            key={v.id}
            className="flex flex-row items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
            style={{
              borderColor: isSelected ? "#5C6BFF" : "#E5E5E5",
              background: isSelected ? "#F5F6FF" : "#FFFFFF",
            }}
          >
            <button
              className="flex flex-row items-center gap-3 flex-1 min-w-0 text-left"
              onClick={() => onSelect(v.id)}
            >
              <div
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{
                  borderColor: isSelected ? "#5C6BFF" : "#D4D4D4",
                  background: isSelected ? "#5C6BFF" : "transparent",
                }}
              >
                {isSelected && (
                  <svg width={10} height={8} viewBox="0 0 10 8" fill="none">
                    <path d="M1 4l3 3 5-6" stroke="#FFFFFF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <div className="w-16 h-10 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                <VehicleImage marque={v.marque} modele={v.modele} coloris={v.coloris} mec={v.mec} alt={v.plate} className="w-full h-full" iconSize={16} />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h3 font-medium truncate" style={{ fontFamily: "Poppins" }}>
                  {v.marque} {v.modele}
                </span>
                <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>
                  {v.plate || "—"}{v.km ? ` · ${v.km.toLocaleString()} km` : ""}
                </span>
              </div>
            </button>
            <button
              onClick={() => onEdit(v)}
              className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
              style={{ color: "#6366f1" }}
            >
              Modifier
            </button>
          </div>
        );
      })}
      {dossierVehicles.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun véhicule dans le dossier</span>
      )}
    </div>
  );
}

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function isImageDoc(d: { type: string; name: string; gcs_path?: string }) {
  return d.type === "image" || IMAGE_EXTENSIONS.test(d.gcs_path || d.name);
}

function PhotoThumbnail({ doc, onClick, onDelete }: { doc: { id: string; name: string; gcs_path?: string }; onClick: () => void; onDelete?: () => void }) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!doc.gcs_path) return;
    const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
    const parts = doc.gcs_path.split("/");
    const parentId = parts[2];
    (isOrdreDoc ? ordresApi.getDocumentUrl(parentId, doc.id) : dossiersApi.getDocumentUrl(parentId, doc.id))
      .then((url) => { if (!cancelled) setThumbUrl(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [doc.gcs_path, doc.id]);

  return (
    <div className="relative w-[100px] h-[100px] flex-shrink-0 group">
      <div
        className="w-full h-full rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
        onClick={onClick}
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <Icon1 name="image" size={20} color="#D4D4D4" />
        )}
      </div>
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
          title="Supprimer"
        >
          <Icon1 name="delete" size={13} color="#DC2626" />
        </button>
      )}
    </div>
  );
}


function DocumentsTab({
  documents,
  onAdd,
  onPreview,
  onDelete,
}: {
  documents: Array<{ id?: string; name: string; type: string; created_at?: string; gcs_path?: string }>;
  onAdd: () => void;
  onPreview: (doc: any) => void;
  onDelete: (doc: any) => void;
}) {
  const photos = documents.filter((d) => d.id && isImageDoc(d as any));
  const files = documents.filter((d) => !isImageDoc(d as any));

  return (
    <div className="flex flex-col p-4 gap-4">
      {/* Photo gallery */}
      {photos.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-h3 font-medium text-neutral-500">Photos ({photos.length})</span>
          <div className="flex flex-row flex-wrap gap-2">
            {photos.map((p) => (
              <PhotoThumbnail key={p.id} doc={p as any} onClick={() => onPreview(p)} onDelete={p.id ? () => onDelete(p) : undefined} />
            ))}
          </div>
        </div>
      )}

      {/* Files list */}
      {files.length > 0 && (
        <div className="flex flex-col gap-1">
          {photos.length > 0 && <span className="text-h3 font-medium text-neutral-500 mb-1">Documents ({files.length})</span>}
          {files.map((d, i) => (
            <div
              key={d.id ?? i}
              className="flex flex-row items-center gap-3 px-2 py-2 rounded-lg hover:bg-neutral-50 cursor-pointer"
              onClick={() => onPreview(d)}
            >
              <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                <Icon1 name="file" size={14} color="#A1A1A1" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h3 font-medium truncate">{d.name}</span>
                {d.created_at && <span className="text-h4 text-neutral-400">{d.created_at}</span>}
              </div>
              <button onClick={(e) => { e.stopPropagation(); onPreview(d); }} className="p-1 hover:bg-neutral-100 rounded flex-shrink-0">
                <Icon1 name="download" size={14} color="#A1A1A1" />
              </button>
              {d.id && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(d); }} className="p-1 hover:bg-red-50 rounded flex-shrink-0" title="Supprimer">
                  <Icon1 name="delete" size={14} color="#DC2626" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {documents.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun document</span>
      )}

      <button
        className="flex flex-row items-center gap-2 text-h3 font-medium text-neutral-600 border border-neutral-200 rounded-lg px-4 py-2 w-fit hover:bg-neutral-50"
        onClick={onAdd}
      >
        <Icon1 name="plus" size={14} color="#525252" />
        Ajouter un document
      </button>
    </div>
  );
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: "carte_grise", label: "Carte grise" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "constat_amiable", label: "Constat amiable" },
  { value: "permis_conduire", label: "Permis de conduire" },
  { value: "devis", label: "Devis" },
  { value: "facture", label: "Facture" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "rapport_expertise", label: "Rapport d'expertise" },
  { value: "photo_vehicule", label: "Photo véhicule" },
  { value: "autre", label: "Autre" },
];

function AddDocumentModal({
  dossierId,
  onClose,
  onSuccess,
}: {
  dossierId: string;
  onClose: () => void;
  onSuccess: (dossier: Dossier) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);

    setClassifying(true);
    try {
      const result = await dossiersApi.classifyDocument(selectedFile);
      if (result.suggested_name) {
        const origExt = selectedFile.name.includes(".") ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")) : "";
        const suggestedHasExt = result.suggested_name.includes(".");
        setDocName(suggestedHasExt ? result.suggested_name : result.suggested_name + origExt);
      }
      if (result.type_key) setDocType(result.type_key);
    } catch {
      // Classification failed — user can still manually select type
    } finally {
      setClassifying(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await dossiersApi.uploadDocument(dossierId, file, docName || undefined, docType || undefined);
      showNotification({ type: "success", message: "Document ajouté" });
      onSuccess(updated);
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <BaseModal
      title="Ajouter un document"
      startIcon={<Icon1 name="upload" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Drop zone */}
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
            dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF, images, documents</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
            }}
          />
        </div>

        {/* AI classification */}
        {classifying && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-h3 text-indigo-600">Analyse du document...</span>
          </div>
        )}

        <TextField label="Nom du document" value={docName} onChange={setDocName} />
        <DropDown
          label="Type de document"
          options={DOCUMENT_TYPE_OPTIONS}
          value={docType}
          onSelect={(v) => setDocType(String(v))}
        />
      </div>

      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label={uploading ? "Envoi..." : "Ajouter"} onClick={handleUpload} disabled={!file || uploading} />
      </div>
    </BaseModal>
  );
}

// ─── Main OrdreModal ───────────────────────────────────────

interface OrdreModalProps {
  ordre: Ordre;
  onClose: () => void;
  onSave: (ordre: Ordre) => void;
  onDelete?: (ordreId: string) => void;
}

export default function OrdreModal({ ordre, onClose, onSave, onDelete }: OrdreModalProps) {
  const [activeTab, setActiveTab] = useState("ordre");
  const [form, setForm] = useState<Ordre>({ ...ordre });
  const [subModal, setSubModal] = useState<string | null>(null);
  const [addGroup, setAddGroup] = useState<string>("initial");
  const [showRestitutionConfirm, setShowRestitutionConfirm] = useState(false);
  const [forceConfirm, setForceConfirm] = useState<SlotValidationResult | null>(null);
  // Remembers that the user chose to force a non-conforming slot, so the force
  // intent survives the "notify client" confirm modal and reaches handleSave.
  const [pendingForce, setPendingForce] = useState(false);
  // ServiceBox slot-collision prediction: does the reception/restitution slot land
  // on a reception column already taken by another RDV? (transfer would be refused)
  const [receptionConflict, setReceptionConflict] = useState<ReceptionConflictResult | null>(null);
  // Occupied slots on the ordre's reception columns — greyed out in the date pickers.
  const [recDisabledSlots, setRecDisabledSlots] = useState<string[]>([]);
  const [restDisabledSlots, setRestDisabledSlots] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInServiceBox, setDeleteInServiceBox] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showAutoPlace, setShowAutoPlace] = useState<"confirm" | "results" | null>(null);
  const [autoPlaceLoading, setAutoPlaceLoading] = useState(false);
  const [autoPlaceResults, setAutoPlaceResults] = useState<{
    placed: { intervention_id: string; label: string; worker_name: string; date?: string; start_hour?: number; duration_hours?: number }[];
    unplaced: { intervention_id: string; label: string; worker_name: string; reason?: string }[];
  } | null>(null);

  const update = <K extends keyof Ordre>(key: K, value: Ordre[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const [parentDossier, setParentDossier] = useState<Dossier | null>(null);
  useEffect(() => {
    if (ordre.dossier_id) {
      dossiersApi.get(ordre.dossier_id).then(setParentDossier).catch(() => {});
    }
  }, [ordre.dossier_id]);

  // Recall campaigns: seed from the ordre's embedded list, then merge what's stored
  // in the recall_campaigns collection (the OR fiche shows these — keep parity).
  const [recalls, setRecalls] = useState<{ code: string; libelle: string; status: string }[]>(
    (ordre.recall_campaigns || []).map((c) => ({ code: c.code, libelle: c.libelle, status: c.status || "detected" })),
  );
  useEffect(() => {
    recallCampaignsApi.listForOrdre(ordre.id)
      .then((stored) => {
        setRecalls((prev) => {
          const byCode = new Map(prev.map((c) => [c.code || c.libelle, c]));
          stored.forEach((s) => {
            const key = s.campaign_code || s.campaign_title;
            if (!byCode.has(key)) byCode.set(key, { code: s.campaign_code, libelle: s.campaign_title, status: s.status });
          });
          return Array.from(byCode.values());
        });
      })
      .catch(() => {});
  }, [ordre.id]);

  const orgData = useDataStore((s) => s.orgData);
  const fetchAtelierData = useAtelierStore((s) => s.fetchData);
  const atelierJobs = useAtelierStore((s) => s.jobs);
  const atelierWorkers = useAtelierStore((s) => s.workers);
  const atelierCharges = useAtelierStore((s) => s.charges);

  const [configuredInterventions, setConfiguredInterventions] = useState<Intervention[]>([]);

  useEffect(() => {
    if (orgData?.id) {
      fetchAtelierData(orgData.id);
      interventionsApi.list(orgData.id).then(setConfiguredInterventions).catch(() => {});
    }
  }, [orgData?.id]);

  // ── Actions menu state (parity with the Ordre detail page) ──
  const [payments, setPayments] = useState<Payment[]>([]);
  const [rentalVehicle, setRentalVehicle] = useState<Vehicle | null>(null);
  const [showRentalModal, setShowRentalModal] = useState(false);
  const [showContratModal, setShowContratModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState<"acompte" | "solde" | null>(null);
  const [showFactureUpload, setShowFactureUpload] = useState(false);
  const [showTrackingPreview, setShowTrackingPreview] = useState(false);
  const [showServiceBoxModal, setShowServiceBoxModal] = useState(false);
  // Fields persisted on the ordre after a successful ServiceBox transfer. The `ordre`
  // prop here is fed by a non-store-subscribed host (planning), so we overlay these
  // locally to refresh the N° OR field and the Actions menu without reopening the modal.
  const [transferUpdates, setTransferUpdates] = useState<Partial<Ordre> | null>(null);
  const currentOrdre = transferUpdates ? ({ ...ordre, ...transferUpdates } as Ordre) : ordre;
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showServiceBoxEstimation, setShowServiceBoxEstimation] = useState(false);
  const [sbExtraction, setSbExtraction] = useState<EstimateExtraction | null>(null);
  const [sbEstimationLoading, setSbEstimationLoading] = useState(false);
  // Forfait variant picker (when the DMS proposes several forfaits for a code).
  const [sbForfaitChoices, setSbForfaitChoices] = useState<ForfaitChoice[] | null>(null);
  const [sbForfaitSel, setSbForfaitSel] = useState<Record<string, string>>({});
  const [sbForfaitSubmitting, setSbForfaitSubmitting] = useState(false);
  const [serviceboxProxyAvailable, setServiceboxProxyAvailable] = useState(false);
  const [flagReasonModal, setFlagReasonModal] = useState<{ flag: OrdreFlag; newFlags: OrdreFlag[] } | null>(null);
  const [flagReasonText, setFlagReasonText] = useState("");

  const hasFacture = useMemo(
    () => (ordre.documents || []).some((d) => d.type?.toLowerCase().replace(" ", "_") === "facture"),
    [ordre.documents],
  );
  const primaryVehicle = ordre.vehicle || (ordre.vehicle_id ? parentDossier?.vehicles?.find((v) => v.id === ordre.vehicle_id) : undefined);

  const fetchPayments = () => { paymentsApi.listForOrdre(ordre.id).then(setPayments).catch(() => {}); };

  useEffect(() => { fetchPayments(); }, [ordre.id]);

  useEffect(() => {
    if (ordre.rental_vehicle_id) {
      vehiclesApi.get(ordre.rental_vehicle_id).then(setRentalVehicle).catch(() => {});
    } else {
      setRentalVehicle(null);
    }
  }, [ordre.rental_vehicle_id]);

  useEffect(() => {
    if (!orgData?.servicebox?.enabled) return;
    let cancelled = false;
    serviceboxProxy.checkHealth().then((r) => { if (!cancelled) setServiceboxProxyAvailable(!!r); }).catch(() => {});
    return () => { cancelled = true; };
  }, [orgData?.servicebox?.enabled]);

  const sbCreds = () => ({ username: orgData?.servicebox?.username || "", password: orgData?.servicebox?.password || "", site_code: orgData?.servicebox?.site_code });

  // The `ordre` prop can be stale (the planning host isn't store-subscribed), so a
  // ServiceBox dossier id set by an earlier transfer may be missing here even though
  // it exists on the backend — which is why this works on the Ordre detail page (fresh
  // store data) but errors from the modal. Resolve from the local overlay first, then
  // re-fetch as a self-healing fallback (also refreshes the store and the overlay).
  const resolveDossierId = async (): Promise<string | undefined> => {
    if (currentOrdre.servicebox_dossier_id) return currentOrdre.servicebox_dossier_id;
    try {
      const fresh = await ordresApi.get(ordre.id);
      useOrdreStore.getState().updateOrdre(fresh);
      setTransferUpdates((prev) => ({ ...(prev || {}), servicebox_dossier_id: fresh.servicebox_dossier_id, servicebox_rdv_id: fresh.servicebox_rdv_id }));
      return fresh.servicebox_dossier_id;
    } catch {
      return undefined;
    }
  };

  // When the DMS asks to choose a forfait variant, open the picker. Returns true if handled.
  const handleForfaitChoiceResult = (result: { needs_forfait_choice?: boolean; forfait_choices?: ForfaitChoice[] }) => {
    if (!result.needs_forfait_choice) return false;
    const choices = result.forfait_choices || [];
    const sel: Record<string, string> = {};
    for (const c of choices) {
      if (!(c.field in sel) || c.selected) sel[c.field] = c.code;
    }
    setSbForfaitChoices(choices);
    setSbForfaitSel(sel);
    setShowServiceBoxEstimation(false);
    return true;
  };

  const importEstimationHtml = async (html: string) => {
    const extraction = await estimateExtractionApi.importServicebox(ordre.id, html);
    setSbExtraction(extraction);
    const refreshed = await ordresApi.get(ordre.id);
    useOrdreStore.getState().updateOrdre(refreshed);
  };

  const submitForfaitChoice = async () => {
    const dossierId = currentOrdre.servicebox_dossier_id;
    if (!orgData?.servicebox || !dossierId) return;
    setSbForfaitSubmitting(true);
    try {
      const selections = Object.entries(sbForfaitSel).map(([field, value]) => ({ field, value }));
      const result = await serviceboxProxy.chooseForfait(sbCreds(), dossierId, selections);
      if (handleForfaitChoiceResult(result)) return; // still ambiguous (another group)
      if (!result.success || !result.html) {
        showNotification({ type: "error", message: result.error || "Impossible de récupérer le devis" });
        return;
      }
      setSbForfaitChoices(null);
      setShowServiceBoxEstimation(true);
      setSbEstimationLoading(true);
      await importEstimationHtml(result.html);
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || e?.message || "Erreur" });
    } finally {
      setSbForfaitSubmitting(false);
      setSbEstimationLoading(false);
    }
  };

  const handleImportServiceBoxEstimation = async () => {
    if (!orgData?.servicebox) return;
    const dossierId = await resolveDossierId();
    if (!dossierId) {
      showNotification({ type: "error", message: "Aucun dossier ServiceBox lié à cet OR. Transférez d'abord vers ServiceBox." });
      return;
    }
    setSbExtraction(null);
    setShowServiceBoxEstimation(true);
    setSbEstimationLoading(true);
    try {
      const result = await serviceboxProxy.fetchEstimation(sbCreds(), dossierId);
      if (handleForfaitChoiceResult(result)) return;
      if (!result.success || !result.html) {
        showNotification({ type: "error", message: result.error || "Impossible de récupérer l'estimation" });
        setShowServiceBoxEstimation(false);
        return;
      }
      await importEstimationHtml(result.html);
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || e?.message || "Erreur" });
      setShowServiceBoxEstimation(false);
    } finally {
      setSbEstimationLoading(false);
    }
  };

  const ordreAtelierCharges = useMemo(() => {
    const workerMap = new Map(atelierWorkers.map((w) => [w.id, w.name]));
    // Placed jobs (on the grid)
    const jobRows: AtelierChargeDisplay[] = atelierJobs
      .filter((j) => j.ordre_id === ordre.id)
      .map((j) => ({
        id: j.id,
        worker_name: workerMap.get(j.workerId) || "—",
        specialty: j.specialty,
        date: j.date,
        start_hour: j.startHour,
        duration_hours: j.durationHours,
        is_placed: true,
      }));
    // Unplaced charges (still in sidebar)
    const chargeRows: AtelierChargeDisplay[] = atelierCharges
      .filter((c) => c.ordre_id === ordre.id)
      .map((c) => ({
        id: c.id,
        worker_name: "Non assigne",
        specialty: c.specialty,
        date: "—",
        start_hour: 0,
        duration_hours: c.hours,
        is_placed: false,
      }));
    return [...jobRows, ...chargeRows];
  }, [atelierJobs, atelierWorkers, atelierCharges, ordre.id]);

  const assignedWorkers = useMemo(() => {
    const seen = new Set<string>();
    return atelierJobs
      .filter((j) => j.ordre_id === ordre.id)
      .reduce<{ name: string; specialty: string }[]>((acc, j) => {
        if (!seen.has(j.workerId)) {
          seen.add(j.workerId);
          const w = atelierWorkers.find((w) => w.id === j.workerId);
          if (w) acc.push({ name: w.name, specialty: w.specialty });
        }
        return acc;
      }, []);
  }, [atelierJobs, atelierWorkers, ordre.id]);

  const [saving, setSaving] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [pendingContactData, setPendingContactData] = useState<{ contact: Contact | null; data: Partial<Contact> } | null>(null);
  // Auto-push a primary-contact client edit to ServiceBox when this ordre is already transferred.
  const [sbAutoUpdateOrdre, setSbAutoUpdateOrdre] = useState<Ordre | null>(null);
  const [editingVehicle, setEditingVehicle] = useState<ClientVehicle | null>(null);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ doc: any; url: string } | null>(null);
  const [docToDelete, setDocToDelete] = useState<{ id: string; name: string; gcs_path?: string } | null>(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  const handleDeleteDocument = async () => {
    if (!docToDelete?.id) return;
    setDeletingDoc(true);
    try {
      const isOrdreDoc = docToDelete.gcs_path?.startsWith("ordres/");
      if (isOrdreDoc) {
        const updated = await ordresApi.deleteDocument(ordre.id, docToDelete.id);
        setForm((prev) => ({ ...prev, documents: updated.documents }));
        useOrdreStore.getState().updateOrdre(updated);
      } else if (parentDossier) {
        const updated = await dossiersApi.deleteDocument(parentDossier.id, docToDelete.id);
        setParentDossier(updated);
      }
      showNotification({ type: "success", message: "Document supprimé" });
      setDocToDelete(null);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la suppression du document" });
    } finally {
      setDeletingDoc(false);
    }
  };

  const openDocPreview = async (doc: { id?: string; gcs_path?: string; name: string }) => {
    if (!doc.id || !doc.gcs_path) return;
    try {
      const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
      const parts = doc.gcs_path.split("/");
      const parentId = parts[2];
      const url = isOrdreDoc
        ? await ordresApi.getDocumentUrl(parentId, doc.id)
        : await dossiersApi.getDocumentUrl(parentId, doc.id);
      setPreviewDoc({ doc, url });
    } catch {
      showNotification({ type: "error", message: "Impossible de charger l'aperçu" });
    }
  };

  const saveContactToDossier = async (contact: Contact | null, data: Partial<Contact>) => {
    if (!parentDossier) return;
    setContactSaving(true);
    const primaryClientChanged = !!contact && contact.is_primary &&
      (["adresse", "nom", "prenom", "phone", "email", "civilite"] as const).some(
        (k) => data[k] !== undefined && data[k] !== contact[k],
      );
    try {
      let updatedContacts: Contact[];
      if (contact) {
        // Editing existing contact
        updatedContacts = (parentDossier.contacts || []).map((c) =>
          c.id === contact.id ? { ...c, ...data } : c
        );
      } else {
        // Adding new contact
        const newContact: Contact = {
          id: `c-new-${Date.now()}`,
          dossier_id: parentDossier.id,
          nom: data.nom || "",
          is_primary: data.is_primary || false,
          created_at: new Date().toISOString(),
          ...data,
        };
        updatedContacts = [...(parentDossier.contacts || []), newContact];
      }
      const updatedDossier = await dossiersApi.update(parentDossier.id, { contacts: updatedContacts } as any);
      setParentDossier(updatedDossier);
      // Re-fetch enriched ordre to update form.contacts
      const refreshedOrdre = await ordresApi.get(ordre.id);
      setForm((prev) => ({ ...prev, contacts: refreshedOrdre.contacts }));
      useOrdreStore.getState().updateOrdre(refreshedOrdre);
      setSubModal(null);
      setEditingContact(null);
      setPendingContactData(null);
      showNotification({ type: "success", message: contact ? "Contact mis à jour" : "Contact ajouté" });
      // Already transferred → push the updated client to its ServiceBox RDV.
      if (primaryClientChanged && orgData?.servicebox?.enabled && refreshedOrdre.servicebox_rdv_id) {
        setSbAutoUpdateOrdre(refreshedOrdre);
      }
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'enregistrement du contact" });
    }
    setContactSaving(false);
  };

  const handleContactSave = (contact: Contact | null, data: Partial<Contact>) => {
    const isPrimaryContact = contact?.is_primary || data.is_primary;
    if (isPrimaryContact && contact) {
      const emailChanged = data.email !== undefined && data.email !== contact.email;
      const phoneChanged = data.phone !== undefined && data.phone !== contact.phone;
      if (emailChanged || phoneChanged) {
        setPendingContactData({ contact, data });
        return;
      }
    }
    if (!contact && data.is_primary && (data.email || data.phone)) {
      setPendingContactData({ contact, data });
      return;
    }
    saveContactToDossier(contact, data);
  };

  const saveVehicleToDossier = async (vehicle: ClientVehicle, data: Partial<ClientVehicle>) => {
    if (!parentDossier) return;
    setVehicleSaving(true);
    try {
      const updatedVehicles = (parentDossier.vehicles || []).map((v) =>
        v.id === vehicle.id ? { ...v, ...data } : v
      );
      const updatedDossier = await dossiersApi.update(parentDossier.id, { vehicles: updatedVehicles } as any);
      setParentDossier(updatedDossier);
      // If this is the selected vehicle, update the form too
      if (form.vehicle_id === vehicle.id || form.vehicle?.id === vehicle.id) {
        const updated = updatedVehicles.find((v) => v.id === vehicle.id);
        if (updated) update("vehicle", updated as any);
      }
      setEditingVehicle(null);
      showNotification({ type: "success", message: "Véhicule modifié" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde du véhicule" });
    } finally {
      setVehicleSaving(false);
    }
  };

  const restitutionChanged =
    form.restitution?.seconds !== ordre.restitution?.seconds;
  const receptionChanged =
    form.reception_date?.seconds !== ordre.reception_date?.seconds;
  // A change worth telling the client about only exists when there was a PRIOR
  // date. Setting a date for the first time (e.g. a fresh RDV) is not a change,
  // so it must not pop the "prévenir le client" confirmation.
  const restitutionNotifiable = !!ordre.restitution?.seconds && restitutionChanged;
  const receptionNotifiable = !!ordre.reception_date?.seconds && receptionChanged;

  // ServiceBox slot-collision check: immediate on open, debounced on later edits.
  const conflictFirstRun = useRef(true);
  useEffect(() => {
    if (!ordre.id) { setReceptionConflict(null); return; }
    const rec = form.reception_date?.seconds ? secondsToDateTime(form.reception_date.seconds) : null;
    const rest = form.restitution?.seconds ? secondsToDateTime(form.restitution.seconds) : null;
    if (!rec && !rest) { setReceptionConflict(null); return; }
    let cancelled = false;
    const run = () => {
      appointmentsApi
        .checkReceptionConflicts({
          ordre_id: ordre.id,
          reception_date: rec?.date,
          reception_time: rec?.time,
          restitution_date: rest?.date,
          restitution_time: rest?.time,
        })
        .then((res) => { if (!cancelled) setReceptionConflict(res); })
        .catch((e) => console.error("reception conflict check failed", e));
    };
    // Run immediately the first time (on open), debounce subsequent date edits.
    if (conflictFirstRun.current) {
      conflictFirstRun.current = false;
      run();
      return;
    }
    const t = setTimeout(run, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [ordre.id, form.reception_date?.seconds, form.restitution?.seconds]);

  // Occupied slots on the reception/restitution columns for the chosen day → grey them out.
  const recDateStr = form.reception_date?.seconds ? secondsToDateTime(form.reception_date.seconds).date : null;
  const restDateStr = form.restitution?.seconds ? secondsToDateTime(form.restitution.seconds).date : null;
  useEffect(() => {
    if (!ordre.id || !recDateStr) { setRecDisabledSlots([]); return; }
    let cancelled = false;
    appointmentsApi.occupiedSlots({ ordre_id: ordre.id, type: "reception", date: recDateStr })
      .then((s) => { if (!cancelled) setRecDisabledSlots(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ordre.id, recDateStr]);
  useEffect(() => {
    if (!ordre.id || !restDateStr) { setRestDisabledSlots([]); return; }
    let cancelled = false;
    appointmentsApi.occupiedSlots({ ordre_id: ordre.id, type: "restitution", date: restDateStr })
      .then((s) => { if (!cancelled) setRestDisabledSlots(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ordre.id, restDateStr]);

  /** Move a colliding event's RDV to the chosen free reception column. */
  const switchReception = async (ev: ReceptionConflictEvent, recId: string) => {
    const free = ev.free_receptions.find((r) => r.id === recId);
    if (!free || !ev.appointment_id) return;
    try {
      await appointmentsApi.update(ev.appointment_id, { category: free.id });
      showNotification({ type: "success", message: `Basculé sur ${free.label}` });
      const ps = usePlanningStore.getState();
      if (ps._fetchedOrgId) ps.fetchAppointments(ps._fetchedOrgId, true);
      const rec = form.reception_date?.seconds ? secondsToDateTime(form.reception_date.seconds) : null;
      const rest = form.restitution?.seconds ? secondsToDateTime(form.restitution.seconds) : null;
      const res = await appointmentsApi.checkReceptionConflicts({
        ordre_id: ordre.id,
        reception_date: rec?.date,
        reception_time: rec?.time,
        restitution_date: rest?.date,
        restitution_time: rest?.time,
      });
      setReceptionConflict(res);
    } catch (e) {
      showNotification({ type: "error", message: "Échec du changement de réception" });
    }
  };

  const handleSave = async (restitutionComment?: string, notify?: boolean, force?: boolean) => {
    setSaving(true);
    try {
      const updated = await ordresApi.update(ordre.id, {
        name: form.name,
        tags: form.tags,
        status: form.status,
        immatriculation: form.immatriculation,
        modele: form.modele,
        campagne: form.campagne,
        assigned_to: form.assigned_to,
        notes: form.notes,
        interventions: form.interventions,
        documents: form.documents,
        reception_date: form.reception_date,
        restitution: form.restitution,
        vehicle_id: form.vehicle_id,
        ...(force ? { force: true } : {}),
      });
      // If restitution date changed, notify client (only if checkbox was checked)
      if (restitutionNotifiable && form.restitution?.seconds && notify !== false) {
        try {
          await ordresApi.notifyRestitutionChange(ordre.id, {
            old_date_seconds: ordre.restitution?.seconds,
            new_date_seconds: form.restitution.seconds,
            comment: restitutionComment || undefined,
            notify,
          });
        } catch (e) {
          console.error("Error notifying restitution change:", e);
        }
      }
      // If reception (drop-off) time changed, notify client too
      if (receptionNotifiable && form.reception_date?.seconds && notify !== false) {
        try {
          await ordresApi.notifyReceptionChange(ordre.id, {
            old_date_seconds: ordre.reception_date?.seconds,
            new_date_seconds: form.reception_date.seconds,
            comment: restitutionComment || undefined,
            notify,
          });
        } catch (e) {
          console.error("Error notifying reception change:", e);
        }
      }
      useOrdreStore.getState().updateOrdre(updated);
      // Refetch planning appointments so the grid reflects updated times
      const planningState = usePlanningStore.getState();
      if (planningState._fetchedOrgId) {
        planningState.fetchAppointments(planningState._fetchedOrgId, true);
      }
      // Invalidate cached car windows so atelier grid picks up new dates
      useAtelierStore.getState().invalidateCarWindows(ordre.id);
      onSave(updated);
      showNotification({ type: "success", message: "Ordre enregistré" });

      // Check if any interventions have assigned workers — offer auto-placement
      const assignedInterventions = form.interventions.filter((i) => i.assigned_to);
      if (assignedInterventions.length > 0) {
        setShowAutoPlace("confirm");
      }
    } catch (e) {
      console.error("Error saving ordre:", e);
      showNotification({ type: "error", message: "Erreur lors de l'enregistrement" });
    } finally {
      setSaving(false);
      setShowRestitutionConfirm(false);
      setPendingForce(false);
    }
  };

  /** Continue the normal save path (restitution-change confirm, or direct save). */
  const proceedSave = (force = false) => {
    // Only ask to notify the client when an EXISTING date actually changed.
    if ((restitutionNotifiable && form.restitution?.seconds) || (receptionNotifiable && form.reception_date?.seconds)) {
      setPendingForce(force);
      setShowRestitutionConfirm(true);
    } else {
      handleSave(undefined, undefined, force);
    }
  };

  const handleSaveClick = async () => {
    const recSec = form.reception_date?.seconds;
    // Validate the slot with the SAME rules as the online widget when dates change
    if ((receptionChanged || restitutionChanged) && recSec) {
      try {
        const dep = secondsToDateTime(recSec);
        const restSec = form.restitution?.seconds;
        const rest = restSec ? secondsToDateTime(restSec) : { date: null, time: null };
        const interventionHours = form.interventions.reduce(
          (sum, i) => sum + (i.estimated_hours || 1.5), 0,
        ) || null;
        const result = await bookingsApi.validateSlot({
          deposit_date: dep.date,
          deposit_time: dep.time,
          service_type: form.campagne || (form.booking_request as any)?.service || "Mécanique",
          intervention_hours: interventionHours,
          restitution_date: rest.date,
          restitution_time: rest.time,
          check_deposit: true,
        });
        if (result.errors.length > 0) {
          setForceConfirm(result);
          return;
        }
      } catch (e) {
        // Fail-open: if validation is unreachable, don't block the staff save
        console.error("validateSlot failed:", e);
      }
    }
    proceedSave();
  };

  /** Apply the validator's suggested restitution time on the reception day. */
  const applySuggestedRestitution = (time: string) => {
    const recSec = form.reception_date?.seconds;
    if (!recSec) return;
    const [hh, mm] = time.split(":").map((x) => parseInt(x, 10));
    const d = new Date(recSec * 1000);
    d.setHours(hh, mm, 0, 0);
    update("restitution", { seconds: Math.floor(d.getTime() / 1000) });
    setForceConfirm(null);
  };

  const handleAutoPlace = async () => {
    if (!orgData?.id) return;
    const assignedInterventions = form.interventions.filter((i) => i.assigned_to);
    if (assignedInterventions.length === 0) return;

    setAutoPlaceLoading(true);
    try {
      const results = await atelierApi.autoPlaceForOrdre({
        org_id: orgData.id,
        ordre_id: ordre.id,
        interventions: assignedInterventions.map((i) => ({
          intervention_id: i.id,
          label: i.label,
          assigned_to: i.assigned_to!,
          estimated_hours: i.estimated_hours || 1,
        })),
      });
      setAutoPlaceResults(results);
      setShowAutoPlace("results");
      // Refresh atelier data
      fetchAtelierData(orgData.id);
    } catch (e) {
      console.error("Auto-place error:", e);
      showNotification({ type: "error", message: "Erreur lors du placement automatique" });
      setShowAutoPlace(null);
      onClose();
    } finally {
      setAutoPlaceLoading(false);
    }
  };

  return (
    <>
      <BaseModal
        title={<>Modifier RDV — {form.num} <span className="text-neutral-100 text-sm font-normal ml-2">{ordre.id}</span></>}
        startIcon={<Icon1 name="planning" size={20} />}
        onClose={onClose}
        width="max-w-4xl"
      >
        {/* Quick nav + Tabs */}
        <div className="px-4 pt-3 overflow-hidden flex flex-col gap-2">
          <div className="flex flex-row items-center gap-3">
            <button
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 w-fit cursor-pointer"
              onClick={() => { onClose(); navigateTo({ route: `/ordre/${ordre.id}` }); }}
            >
              <Icon1 name="open" size={14} />
              Ouvrir la fiche ordre
            </button>
            {ordre.dossier_id && (
              <button
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 w-fit cursor-pointer"
                onClick={() => { onClose(); navigateTo({ route: `/dossier/${ordre.dossier_id}` }); }}
              >
                <Icon1 name="open" size={14} />
                Ouvrir le dossier
              </button>
            )}
          </div>
          <div className="flex flex-row items-center justify-between gap-3">
            <ChunkyTabs
              activeTab={activeTab}
              tabs={TABS}
              setActiveTab={setActiveTab}
            />
            <OrdreActionsMenu
              ordre={currentOrdre}
              orgData={orgData}
              context="modal"
              payments={payments}
              rentalVehicle={rentalVehicle}
              hasFacture={hasFacture}
              serviceboxProxyAvailable={serviceboxProxyAvailable}
              sbEstimationLoading={sbEstimationLoading}
              onEdit={() => {}}
              onDelete={() => setShowDeleteConfirm(true)}
              onRequestFlagReason={(flag, newFlags) => { setFlagReasonText(""); setFlagReasonModal({ flag, newFlags }); }}
              onShowReminder={() => setShowReminderModal(true)}
              onAddDocument={() => setSubModal("document")}
              onAddFacture={() => setShowFactureUpload(true)}
              onServiceBoxTransfer={() => setShowServiceBoxModal(true)}
              onImportEstimation={() => { handleImportServiceBoxEstimation(); }}
              onShowSecondary={() => setSubModal("secondary")}
              onShowRental={() => setShowRentalModal(true)}
              onChangeRental={async () => {
                if (ordre.rental_reservation_id) { try { await vehiclesApi.deleteReservation(ordre.rental_reservation_id); } catch {} }
                const updatedOrdre = await ordresApi.update(ordre.id, { rental_vehicle_id: "", rental_reservation_id: "" });
                useOrdreStore.getState().updateOrdre(updatedOrdre);
                setRentalVehicle(null);
                setShowRentalModal(true);
              }}
              onRemoveRental={async () => {
                try {
                  if (ordre.rental_reservation_id) await vehiclesApi.deleteReservation(ordre.rental_reservation_id);
                  const updatedOrdre = await ordresApi.update(ordre.id, { rental_vehicle_id: "", rental_reservation_id: "" });
                  useOrdreStore.getState().updateOrdre(updatedOrdre);
                  setRentalVehicle(null);
                  showNotification({ type: "success", message: "Véhicule de remplacement retiré" });
                } catch { showNotification({ type: "error", message: "Erreur" }); }
              }}
              onShowContrat={() => setShowContratModal(true)}
              onShowTracking={() => setShowTrackingPreview(true)}
              onShowPayment={(type) => setShowPaymentModal(type)}
            />
          </div>
        </div>

        {/* Tab content — fixed height so the modal doesn't resize between tabs */}
        <div className="h-[480px] overflow-y-auto overflow-x-hidden">
          {activeTab === "ordre" && (
            <OrdreTab
              form={form}
              update={update}
              assignedWorkers={assignedWorkers}
              linkedVehicle={form.vehicle || parentDossier?.vehicles?.find((v) => v.id === form.vehicle_id) || null}
              dossierType={parentDossier?.type as DossierType | undefined}
              onDossierTypeChange={async (type) => {
                if (!parentDossier) return;
                const updated = await dossiersApi.update(parentDossier.id, { type: type || "particulier" } as any);
                setParentDossier(updated);
              }}
              recalls={recalls}
              receptionConflict={receptionConflict}
              onSwitchReception={switchReception}
              receptionDisabledSlots={recDisabledSlots}
              restitutionDisabledSlots={restDisabledSlots}
            />
          )}
          {activeTab === "interventions" && (
            <InterventionsTab
              interventions={form.interventions}
              interventionGroups={form.intervention_groups}
              atelierCharges={ordreAtelierCharges}
              onAdd={(g) => { setAddGroup(g); setSubModal("intervention"); }}
              onRemove={(idx) => update("interventions", form.interventions.filter((_, i) => i !== idx))}
            />
          )}
          {activeTab === "contacts" && (
            <ContactsTab
              contacts={form.contacts || []}
              onAdd={() => { setEditingContact(null); setSubModal("contact"); }}
              onEdit={(c) => { setEditingContact(c); setSubModal("contact"); }}
            />
          )}
          {activeTab === "vehicules" && (
            <VehiculesTab
              dossierVehicles={parentDossier?.vehicles || (form.vehicle ? [form.vehicle] : [])}
              selectedVehicleId={form.vehicle_id || form.vehicle?.id}
              onSelect={(vehicleId) => {
                const v = (parentDossier?.vehicles || []).find((veh) => veh.id === vehicleId);
                update("vehicle_id", vehicleId);
                if (v) {
                  update("vehicle", v);
                  if (v.plate) update("immatriculation", v.plate);
                  const vehicleModele = [v.marque, v.modele].filter(Boolean).join(" ");
                  if (vehicleModele) update("modele", vehicleModele);
                }
              }}
              onEdit={(v) => setEditingVehicle(v)}
            />
          )}
          {activeTab === "documents" && (
            <DocumentsTab
              documents={parentDossier?.documents || form.documents || []}
              onAdd={() => setSubModal("document")}
              onPreview={openDocPreview}
              onDelete={(doc) => setDocToDelete(doc)}
            />
          )}
          {activeTab === "communications" && (
            <div style={{ height: "100%" }}>
              <ChatPanel
                discussionId={parentDossier?.discussion_id || ordre.discussion_id}
                internalDiscussionId={parentDossier?.internal_discussion_id || ordre.internal_discussion_id}
                entityType="ordre"
                entityId={ordre.id}
                entityName={ordre.name}
                compact
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-1 p-4 flex flex-row justify-end">
          <div className="flex flex-row gap-2">
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="copy" size={14} />}
              onClick={() => {
                navigator.clipboard.writeText(ordre.id);
                showNotification({ type: "default", message: "ID ordre copié" });
              }}
            >
              Copier ID
            </SecondaryButton>
            <SecondaryButton variant="small" onClick={onClose}>
              Fermer
            </SecondaryButton>
            <Button variant="small" label={saving ? "Enregistrement..." : "Enregistrer"} onClick={handleSaveClick} disabled={saving || !form.restitution?.seconds} />
          </div>
        </div>

      </BaseModal>

      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer l'ordre ${form.num} ?`}
          message="Cette action est irréversible. L'ordre et ses rendez-vous associés seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          extraContent={ordre.servicebox_rdv_id ? (
            <div className="flex flex-row items-center gap-3 p-2.5 rounded-lg bg-neutral-50 border border-neutral-200">
              <div className="flex flex-col flex-1">
                <span className="text-h4 font-medium text-neutral-800">Supprimer aussi dans ServiceBox</span>
                <span className="text-h5 text-neutral-400">Annule également le RDV dans le planning ServiceBox</span>
              </div>
              <Switch checked={deleteInServiceBox} onChange={setDeleteInServiceBox} />
            </div>
          ) : undefined}
          onConfirm={async () => {
            setDeleting(true);
            try {
              // Optionally delete the linked RDV in ServiceBox first (best-effort).
              if (deleteInServiceBox && ordre.servicebox_rdv_id && orgData?.servicebox) {
                const sb = orgData.servicebox;
                try {
                  await serviceboxProxy.deleteRdv({ username: sb.username || "", password: sb.password || "", site_code: sb.site_code }, ordre.servicebox_rdv_id);
                } catch (e: any) {
                  showNotification({ type: "error", message: `Suppression ServiceBox échouée : ${e?.message || "erreur"}` });
                }
              }
              await ordresApi.delete(ordre.id);
              const planningState = usePlanningStore.getState();
              if (planningState._fetchedOrgId) {
                planningState.fetchAppointments(planningState._fetchedOrgId, true);
              }
              const atelierState = useAtelierStore.getState();
              if (atelierState._fetchedOrgId) {
                atelierState.fetchData(atelierState._fetchedOrgId, true);
              }
              showNotification({ type: "success", message: "Ordre supprimé" });
              onDelete?.(ordre.id);
              onClose();
            } catch {
              showNotification({ type: "error", message: "Erreur lors de la suppression" });
            }
            setDeleting(false);
          }}
          confirmText="CONFIRMER"
        />
      )}

      {showFactureUpload && (
        <FactureUploadModal ordreId={ordre.id} onClose={() => setShowFactureUpload(false)} onSuccess={(updated) => { useOrdreStore.getState().updateOrdre(updated); setShowFactureUpload(false); }} />
      )}
      {showTrackingPreview && (
        <TrackingPreviewModal ordre={ordre} parentDossier={parentDossier} orgData={orgData} onClose={() => setShowTrackingPreview(false)} />
      )}
      {showRentalModal && orgData?.id && (
        <RentalVehicleModal ordre={ordre} orgId={orgData.id} onClose={() => setShowRentalModal(false)} onAssign={(v) => { setRentalVehicle(v); setShowContratModal(true); }} />
      )}
      {showContratModal && rentalVehicle && orgData && (
        <ContratLocationModal
          ordre={ordre}
          rentalVehicle={rentalVehicle}
          contact={ordre.contacts?.find((c) => c.is_primary) || ordre.contacts?.[0] || parentDossier?.contacts?.find((c) => c.is_primary) || parentDossier?.contacts?.[0]}
          clientVehicle={primaryVehicle}
          orgData={orgData}
          onClose={() => setShowContratModal(false)}
        />
      )}
      {showServiceBoxEstimation && (
        <EstimateExtractionModal ordre={ordre} initialExtraction={sbExtraction || undefined} isExtracting={sbEstimationLoading} onClose={() => { setShowServiceBoxEstimation(false); setSbExtraction(null); }} />
      )}
      {sbForfaitChoices && sbForfaitChoices.length > 0 && (
        <BaseModal onClose={() => setSbForfaitChoices(null)} title="Choisir le forfait" startIcon={<Icon1 name="sett-servicebox" size={20} />} width="max-w-lg">
          <div className="flex flex-col gap-3 p-4">
            <span className="text-h3 text-neutral-500">ServiceBox propose plusieurs forfaits. Choisissez la variante à appliquer :</span>
            {Object.entries(sbForfaitChoices.reduce<Record<string, ForfaitChoice[]>>((acc, c) => { (acc[c.field] ||= []).push(c); return acc; }, {})).map(([field, opts]) => (
              <div key={field} className="flex flex-col gap-1.5">
                {opts.map((c) => {
                  const checked = sbForfaitSel[field] === c.code;
                  return (
                    <label key={c.code} className="flex flex-row items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer" style={{ border: checked ? "2px solid #000" : "1.5px solid #E5E5E5", background: checked ? "#F5F5F5" : "#fff" }}>
                      <input type="radio" name={field} checked={checked} onChange={() => setSbForfaitSel((p) => ({ ...p, [field]: c.code }))} />
                      <div className="flex flex-col flex-1">
                        <span className="text-h3 font-medium text-neutral-700">{c.code} — {c.designation}</span>
                        <span className="text-h4 text-neutral-400">{c.prix_ttc} € TTC{c.prix_ht ? ` · ${c.prix_ht} € HT` : ""}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            ))}
            <div className="flex flex-row gap-2 pt-1">
              <Button label={sbForfaitSubmitting ? "Application…" : "Valider et générer le devis"} onClick={submitForfaitChoice} disabled={sbForfaitSubmitting} variant="small" />
              <SecondaryButton label="Annuler" onClick={() => setSbForfaitChoices(null)} variant="small" />
            </div>
          </div>
        </BaseModal>
      )}
      {showPaymentModal && (
        <PaymentRequestModal ordre={ordre} defaultType={showPaymentModal} onClose={() => setShowPaymentModal(null)} onSuccess={() => { fetchPayments(); ordresApi.get(ordre.id).then((u) => useOrdreStore.getState().updateOrdre(u)).catch(() => {}); }} />
      )}
      {showServiceBoxModal && orgData?.servicebox && (
        <ServiceBoxTransferModal
          ordre={currentOrdre}
          serviceboxConfig={orgData.servicebox}
          onClose={() => setShowServiceBoxModal(false)}
          onTransferred={(updates) => {
            setTransferUpdates((prev) => ({ ...(prev || {}), ...updates }));
            setForm((prev) => ({ ...prev, ...updates }));
          }}
        />
      )}
      {sbAutoUpdateOrdre && orgData?.servicebox && (
        <ServiceBoxTransferModal
          autoRun
          ordre={sbAutoUpdateOrdre}
          serviceboxConfig={orgData.servicebox}
          onClose={() => setSbAutoUpdateOrdre(null)}
        />
      )}
      {showReminderModal && ordre?.intervention_groups && (() => {
        const ig = ordre.intervention_groups;
        const activeToken = Object.values(ig).map((g) => g.devis_token).filter(Boolean).pop();
        return activeToken ? <DevisReminderModal onClose={() => setShowReminderModal(false)} tokenId={activeToken} /> : null;
      })()}
      {flagReasonModal && (
        <BaseModal
          title={`Drapeau « ${ORDRE_FLAG_CONFIG[flagReasonModal.flag].label} »`}
          startIcon={<Icon1 name={ORDRE_FLAG_CONFIG[flagReasonModal.flag].icon} size={20} color={ORDRE_FLAG_CONFIG[flagReasonModal.flag].color} />}
          onClose={() => setFlagReasonModal(null)}
        >
          <div className="flex flex-col gap-3 p-4">
            <div className="text-h3 text-neutral-500">Raison (optionnel)</div>
            <input
              type="text"
              value={flagReasonText}
              onChange={(e) => setFlagReasonText(e.target.value)}
              placeholder="Ex : en attente de pièces..."
              className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              autoFocus
            />
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={() => setFlagReasonModal(null)}>Annuler</SecondaryButton>
            <Button
              variant="small"
              label="Confirmer"
              onClick={async () => {
                try {
                  const updated = await ordresApi.update(ordre.id, {
                    flags: flagReasonModal.newFlags,
                    ...(flagReasonText ? { flag_reason: flagReasonText } : {}),
                  } as any);
                  useOrdreStore.getState().updateOrdre(updated);
                  showNotification({ type: "success", message: `Drapeau « ${ORDRE_FLAG_CONFIG[flagReasonModal.flag].label} » ajouté` });
                } catch {
                  showNotification({ type: "error", message: "Erreur" });
                }
                setFlagReasonModal(null);
              }}
            />
          </div>
        </BaseModal>
      )}

      {/* Sub-modals */}
      {subModal === "intervention" && (
        <InterventionPickerModal
          configuredInterventions={configuredInterventions}
          onCatalogUpdated={setConfiguredInterventions}
          group={addGroup}
          onAdd={(inters) => update("interventions", [...form.interventions, ...inters])}
          onClose={() => setSubModal(null)}
        />
      )}
      {subModal === "contact" && (
        <ContactFormModal
          contact={editingContact}
          onClose={() => { setSubModal(null); setEditingContact(null); }}
          onSave={(data) => handleContactSave(editingContact, data)}
          saving={contactSaving}
        />
      )}
      {pendingContactData && (
        <ConfirmationModal
          title="Mettre à jour les coordonnées ?"
          message="Les coordonnées de ce contact principal sont utilisées pour les notifications, e-mails et messages WhatsApp. Confirmez-vous la modification ?"
          confirmLabel="Oui, mettre à jour"
          onClose={() => setPendingContactData(null)}
          onConfirm={() => { saveContactToDossier(pendingContactData.contact, pendingContactData.data); }}
        />
      )}
      {subModal === "vehicle" && <AddVehicleModal onClose={() => setSubModal(null)} />}
      {editingVehicle && (
        <VehicleEditModal
          vehicle={editingVehicle as ClientVehicle}
          onSave={(data) => saveVehicleToDossier(editingVehicle as ClientVehicle, data)}
          onClose={() => setEditingVehicle(null)}
          saving={vehicleSaving}
          timeline={parentDossier?.timeline || []}
          ordres={parentDossier?.ordres || []}
        />
      )}
      {subModal === "document" && ordre.dossier_id && (
        <AddDocumentModal
          dossierId={ordre.dossier_id}
          onClose={() => setSubModal(null)}
          onSuccess={(updatedDossier) => setParentDossier(updatedDossier)}
        />
      )}
      {docToDelete && (
        <ConfirmationModal
          title="Supprimer ce document ?"
          message={`Le document « ${docToDelete.name} » sera définitivement supprimé. Cette action est irréversible.`}
          confirmLabel={deletingDoc ? "Suppression..." : "Supprimer"}
          onClose={() => { if (!deletingDoc) setDocToDelete(null); }}
          onConfirm={handleDeleteDocument}
        />
      )}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc.doc}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => {
            const a = document.createElement("a");
            a.href = previewDoc.url;
            a.download = previewDoc.doc.name;
            a.target = "_blank";
            a.click();
          }}
        />
      )}
      {showRestitutionConfirm && (restitutionChanged && form.restitution?.seconds ? (
        <RestitutionChangeConfirmModal
          kind="restitution"
          oldSeconds={ordre.restitution?.seconds}
          newSeconds={form.restitution.seconds}
          onClose={() => { setShowRestitutionConfirm(false); setPendingForce(false); }}
          onConfirm={(comment, notify) => handleSave(comment, notify, pendingForce)}
          saving={saving}
        />
      ) : receptionChanged && form.reception_date?.seconds ? (
        <RestitutionChangeConfirmModal
          kind="reception"
          oldSeconds={ordre.reception_date?.seconds}
          newSeconds={form.reception_date.seconds}
          onClose={() => { setShowRestitutionConfirm(false); setPendingForce(false); }}
          onConfirm={(comment, notify) => handleSave(comment, notify, pendingForce)}
          saving={saving}
        />
      ) : null)}
      {forceConfirm && (
        <ForceConfirmModal
          title="Créneau non conforme"
          message={forceConfirm.errors[0]?.message || "Ce créneau ne respecte pas les règles de prise de RDV."}
          confirmLabel="Forcer ce RDV"
          onCancel={() => setForceConfirm(null)}
          onConfirm={() => {
            setForceConfirm(null);
            // Route through proceedSave so forcing still asks whether to notify
            // the client of the reception/restitution change before saving.
            proceedSave(true);
          }}
        >
          {forceConfirm.suggested_restitution_time && form.restitution?.seconds && (
            <button
              className="self-start text-h4 font-medium px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50"
              style={{ fontFamily: "Poppins", color: "#4F46E5" }}
              onClick={() => applySuggestedRestitution(forceConfirm.suggested_restitution_time!)}
            >
              Appliquer la restitution suggérée ({forceConfirm.suggested_restitution_time})
            </button>
          )}
        </ForceConfirmModal>
      )}
      {subModal === "secondary" && (
        <SecondaryOrdreModal
          ordre={ordre}
          onClose={() => { setSubModal(null); onClose(); }}
        />
      )}

      {/* Auto-place confirmation */}
      {showAutoPlace === "confirm" && (
        <BaseModal
          title="Planification atelier"
          startIcon={<Icon1 name="planning" size={20} />}
          onClose={() => { setShowAutoPlace(null); onClose(); }}
        >
          <div className="p-4 flex flex-col gap-3">
            <p className="text-h3 text-neutral-700">
              {form.interventions.filter((i) => i.assigned_to).length} intervention(s) assignée(s).
              Placer automatiquement sur le planning atelier ?
            </p>
            <div className="text-h4 text-neutral-400">
              Le système trouvera le meilleur créneau disponible pour chaque mécanicien assigné,
              en respectant les disponibilités et la charge existante.
            </div>
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={() => { setShowAutoPlace(null); onClose(); }}>
              Non merci
            </SecondaryButton>
            <Button
              variant="small"
              label={autoPlaceLoading ? "Placement en cours..." : "Placer automatiquement"}
              onClick={handleAutoPlace}
              disabled={autoPlaceLoading}
            />
          </div>
        </BaseModal>
      )}

      {/* Auto-place results */}
      {showAutoPlace === "results" && autoPlaceResults && (
        <BaseModal
          title="Résultat du placement"
          startIcon={<Icon1 name="planning" size={20} />}
          onClose={() => { setShowAutoPlace(null); onClose(); }}
        >
          <div className="p-4 flex flex-col gap-4">
            {autoPlaceResults.placed.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-h3 font-medium text-green-700">
                  {autoPlaceResults.placed.length} intervention(s) placée(s)
                </div>
                {autoPlaceResults.placed.map((r) => (
                  <div key={r.intervention_id} className="flex flex-col bg-green-50 rounded-lg px-3 py-2">
                    <span className="text-h3 font-medium">{r.label}</span>
                    <span className="text-h4 text-neutral-600">
                      {r.worker_name} — {r.date ? new Date(r.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : ""} de {formatHour(r.start_hour || 0)} à {formatHour((r.start_hour || 0) + (r.duration_hours || 0))}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {autoPlaceResults.unplaced.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-h3 font-medium text-orange-600">
                  {autoPlaceResults.unplaced.length} intervention(s) sans créneau
                </div>
                {autoPlaceResults.unplaced.map((r) => (
                  <div key={r.intervention_id} className="flex flex-col bg-orange-50 rounded-lg px-3 py-2">
                    <span className="text-h3 font-medium">{r.label}</span>
                    <span className="text-h4 text-neutral-600">{r.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end">
            <Button variant="small" label="Fermer" onClick={() => { setShowAutoPlace(null); onClose(); }} />
          </div>
        </BaseModal>
      )}
    </>
  );
}
